/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { branch, compose, mapProps } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Avatar, Grid, Typography2 } from '@coursera/cds-core';
import { FadeInUp } from '@coursera/coursera-ui';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import type { Testimonial } from 'bundles/payments/components/socialProofCheckoutExperiment/assets/userTestimonials';
import { getTestimonial } from 'bundles/payments/components/socialProofCheckoutExperiment/assets/userTestimonials';
import type PaymentFormStoreType from 'bundles/payments/stores/PaymentFormStore';
import type ApplicationStoreType from 'bundles/ssr/stores/ApplicationStore';

import _t from 'i18n!nls/payments';

import 'css!./__styles__/UserTestimonial';

const styles = {
  cdsCheckoutTestimonialContainer: css`
    background-color: var(--neutral-background-primary, #fff);
    padding: 24px;
    border-radius: 16px;
    border: 1px solid #e5e7e8;
    margin: 0;
    width: auto;
  `,
};

const NORMAL_CONTENT_CLASS = 'normal-content-container';
const CONDENSED_CONTENT_CLASS = 'condensed-content-container';
const CONDENSED_CONTENT_WIDTH_BREAKPOINT = 200;

const NORMAL_TESTIMONIAL_IMAGE_WIDTH = 144;
const NORMAL_TESTIMONIAL_IMAGE_HEIGHT = 192;

type InputProps = {
  disableAnimations?: boolean;
  width?: number;
  height?: number;
  renderCondenseTestimonial?: boolean;
  testimonialOverride?: Testimonial;

  // This prop should only be set to true in the payments app.
  disableDeferRenderToCSR?: boolean;
};

type PropsFromStore = {
  randomNumber?: number;
  showCdsDesign?: boolean;
};

type Stores = { PaymentFormStore: PaymentFormStoreType; ApplicationStore: ApplicationStoreType };

type Props = PropsFromStore &
  Omit<InputProps, 'testimonialOverride'> & {
    testimonial: Testimonial;
  };

type State = {
  hasMounted: boolean;
  forceCondensedTestimonial: boolean;
};

class UserTestimonial extends React.Component<Props, State> {
  declare wrapperRef: HTMLDivElement | null | undefined;

  static defaultProps = {
    width: 144,
    renderCondenseTestimonial: false,
  };

  state = {
    hasMounted: false,
    forceCondensedTestimonial: false,
  };

  componentDidMount() {
    this.setState(() => ({ hasMounted: true }));
    window.addEventListener('resize', this.checkWidthForCondensedTestimonial);
    setTimeout(this.checkWidthForCondensedTestimonial, 0);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkWidthForCondensedTestimonial);
  }

  checkWidthForCondensedTestimonial = throttle(() => {
    if (!this.wrapperRef) {
      return;
    }

    const { width } = this.wrapperRef.getBoundingClientRect();
    const { forceCondensedTestimonial } = this.state;

    // Regular testimonial looks really bad below the breakpoint width, so force showing the condensed version
    if (width < CONDENSED_CONTENT_WIDTH_BREAKPOINT && !forceCondensedTestimonial) {
      this.setState(() => ({ forceCondensedTestimonial: true }));
    } else if (width >= CONDENSED_CONTENT_WIDTH_BREAKPOINT && forceCondensedTestimonial) {
      this.setState(() => ({ forceCondensedTestimonial: false }));
    }
  }, 200);

  renderNormalContent(testimonial: Testimonial): JSX.Element {
    const { width, height, disableDeferRenderToCSR, randomNumber } = this.props;
    const ProfileImageComponent = disableDeferRenderToCSR && Boolean(randomNumber) ? Imgix : LazyImgix;

    return (
      <div className={NORMAL_CONTENT_CLASS} data-testid="normal-testimonial">
        <div className="profile-image" style={{ width: `${width}px` }}>
          <ProfileImageComponent
            src={testimonial.photoUrl}
            imgParams={{ fit: 'clip', crop: 'faces', auto: 'format,compress' }}
            maxWidth={width || NORMAL_TESTIMONIAL_IMAGE_WIDTH}
            maxHeight={height || NORMAL_TESTIMONIAL_IMAGE_HEIGHT}
            alt=""
          />
        </div>
        <div className="caption-container" style={{ maxWidth: `calc(100% - ${width}px)` }}>
          <p className="quote caption-text">{testimonial.caption}</p>
          <div className="user-name caption-text">{`\u2014 ${testimonial.userName}`}</div>
        </div>
      </div>
    );
  }

  renderCondenseTestimonial(testimonial: Testimonial): JSX.Element {
    const { height, disableDeferRenderToCSR, randomNumber } = this.props;
    const ProfileImageComponent = disableDeferRenderToCSR && Boolean(randomNumber) ? Imgix : LazyImgix;
    return (
      <div className={CONDENSED_CONTENT_CLASS} data-testid="condensed-testimonial">
        <div className="profile-image">
          <ProfileImageComponent
            src={testimonial.photoUrl}
            imgParams={{ fit: 'clip', crop: 'faces', auto: 'format,compress' }}
            maxWidth={64}
            maxHeight={height || NORMAL_TESTIMONIAL_IMAGE_HEIGHT}
            alt={_t('User photo')}
          />
        </div>
        <div className="caption-container">
          <p className="quote caption-text">{testimonial.caption}</p>
          <div className="user-name caption-text">{`\u2014 ${testimonial.userName}`}</div>
        </div>
      </div>
    );
  }

  renderCdsCheckoutTestimonial(testimonial: Testimonial): JSX.Element {
    return (
      <Grid container css={styles.cdsCheckoutTestimonialContainer} spacing={16}>
        <Grid container item direction="row" alignItems="center" spacing={12}>
          <Grid item>
            <Avatar imgProps={{ src: testimonial.photoUrl }} size={64} variant="user" />
          </Grid>
          <Grid item>
            <Typography2 component="div" variant="subtitleMedium">
              {testimonial.userName}
            </Typography2>
          </Grid>
        </Grid>
        <Grid item>
          <Typography2 component="div" variant="bodyPrimary">
            <FormattedMessage message={_t('"{caption}"')} caption={testimonial.caption} />
          </Typography2>
        </Grid>
      </Grid>
    );
  }

  render() {
    const { hasMounted } = this.state;
    const {
      renderCondenseTestimonial,
      disableAnimations,
      testimonial,
      disableDeferRenderToCSR,
      randomNumber,
      showCdsDesign,
    } = this.props;
    const { forceCondensedTestimonial } = this.state;

    // `getTestimonial()` returns a testimonial selected at random
    // The SSR selected testimonial is not guaranteed to match the CSR selected one
    // So only render a height placeholder on SSR instead of throwing away the markup
    // We will skip this if we set disableDeferRenderToCSR to true and there is a random number
    // passed in from the store.
    if ((!disableDeferRenderToCSR || !randomNumber) && !hasMounted) {
      const ssrContentClass = classNames('hidden', {
        [CONDENSED_CONTENT_CLASS]: renderCondenseTestimonial,
        [NORMAL_CONTENT_CLASS]: !renderCondenseTestimonial,
      });
      return (
        <div className="rc-UserTestimonial">
          <div className={ssrContentClass} />
        </div>
      );
    }

    const NormalContentContainer = disableAnimations ? 'div' : FadeInUp;

    let content = null;

    if (showCdsDesign) {
      content = this.renderCdsCheckoutTestimonial(testimonial);
    } else if (renderCondenseTestimonial || forceCondensedTestimonial) {
      content = this.renderCondenseTestimonial(testimonial);
    } else {
      content = <NormalContentContainer>{this.renderNormalContent(testimonial)}</NormalContentContainer>;
    }

    return (
      <div
        className="rc-UserTestimonial"
        ref={(ref) => {
          this.wrapperRef = ref;
        }}
      >
        <div data-e2e="user-testimonial">{content}</div>
      </div>
    );
  }
}

export const BaseComponent = UserTestimonial;

export default compose<Props, InputProps>(
  branch<InputProps>(
    ({ disableDeferRenderToCSR }) => Boolean(disableDeferRenderToCSR),
    connectToStores<PropsFromStore, InputProps, Stores>(
      ['PaymentFormStore', 'ApplicationStore'],
      ({ PaymentFormStore, ApplicationStore }) => {
        const appName = ApplicationStore.getState().appName;
        const randomNumber = appName === 'payments' ? PaymentFormStore.getRandomNumber() : undefined;
        return {
          randomNumber,
          showCdsDesign: PaymentFormStore.getSettings().showRedesignedCheckoutPage,
        };
      }
    )
  ),
  mapProps<Props, InputProps & PropsFromStore>(
    ({ testimonialOverride, renderCondenseTestimonial, randomNumber, ...rest }) => {
      const testimonial = testimonialOverride || getTestimonial(renderCondenseTestimonial, randomNumber);
      return {
        testimonial,
        renderCondenseTestimonial,
        randomNumber,
        ...rest,
      };
    }
  )
)(UserTestimonial);
